// Image upload constants matching backend
export const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024; // 5 MB
export const MAX_IMAGE_SIZE_MB = 5;

export const ALLOWED_IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.heic', '.heif'];
export const ALLOWED_IMAGE_MIME_TYPES = ['image/jpeg', 'image/png', 'image/heic', 'image/heif'];

/**
 * Validates an image file before upload
 * @param {File} file - The file to validate
 * @returns {{ valid: boolean, errorKey: string | null, errorParams: object | null }} - Validation result with translation key
 */
export const validateImageFile = (file) => {
  if (!file) {
    return { valid: false, errorKey: 'stores.images.error.noFile', errorParams: null };
  }

  if (file.size === 0) {
    return { valid: false, errorKey: 'stores.images.error.fileEmpty', errorParams: null };
  }

  // Validate file size
  if (file.size > MAX_IMAGE_SIZE_BYTES) {
    return {
      valid: false,
      errorKey: 'stores.images.error.fileTooLarge',
      errorParams: { maxSize: MAX_IMAGE_SIZE_MB }
    };
  }

  // Validate file extension
  const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
  if (!ALLOWED_IMAGE_EXTENSIONS.includes(fileExtension)) {
    return {
      valid: false,
      errorKey: 'stores.images.error.invalidFileType',
      errorParams: { formats: ALLOWED_IMAGE_EXTENSIONS.join(', ').toUpperCase() }
    };
  }

  // Validate MIME type
  if (!ALLOWED_IMAGE_MIME_TYPES.includes(file.type.toLowerCase())) {
    return {
      valid: false,
      errorKey: 'stores.images.error.invalidMimeType',
      errorParams: { mimeTypes: ALLOWED_IMAGE_MIME_TYPES.join(', ') }
    };
  }

  return { valid: true, errorKey: null, errorParams: null };
};

/**
 * Validates multiple image files
 * @param {File[]} files - Array of files to validate
 * @param {Function} t - Translation function
 * @returns {{ valid: boolean, errors: string[] }} - Validation result with translated error messages
 */
export const validateImageFiles = (files, t) => {
  const errors = [];
  
  files.forEach((file, index) => {
    const validation = validateImageFile(file);
    if (!validation.valid) {
      const errorMessage = validation.errorParams 
        ? t(validation.errorKey, validation.errorParams)
        : t(validation.errorKey);
      const formattedError = t('stores.images.error.fileValidationError', {
        index: index + 1,
        fileName: file.name,
        error: errorMessage
      });
      errors.push(formattedError);
    }
  });

  return {
    valid: errors.length === 0,
    errors
  };
};

